import { ref, computed } from 'vue';
import type { RequestContent } from '../types';
import type { CollectionItemInterface } from '@/store/useCollectionsStore';
import { useRequestContentStore } from '@/store/useRequestContentStore';
import { MODAL_VIEWS, SLIDE_CLASSES, type ModalView } from '../constants/constants';

interface UseRequestContentModalSharedProps {
  item?: RequestContent | null;
  onClose?: () => void;
}

export const useRequestContentModalShared = (props: UseRequestContentModalSharedProps) => {
  // Store access
  const requestContentStore = useRequestContentStore();

  // Modal state management
  const currentView = ref<ModalView>(MODAL_VIEWS.FORM);
  const savedItem = ref<RequestContent | null>(props.item || null);
  const showCollectionPicker = ref(false);
  const selectedCollectionId = ref<string | null>(props.item?.collectionId || null);
  const selectedCollection = ref<CollectionItemInterface | null>(null);
  const previousView = ref<ModalView | null>(null); // Track where edit URL was accessed from

  // Slide animation classes
  const slideClass = computed(() => {
    const classes = [];
    if (showCollectionPicker.value) {
      classes.push(SLIDE_CLASSES.COLLECTION);
    } else {
      switch (currentView.value) {
        case MODAL_VIEWS.SUCCESS:
          classes.push(SLIDE_CLASSES.SUCCESS);
          break;
        case MODAL_VIEWS.EDIT_URL:
          classes.push(SLIDE_CLASSES.EDIT_URL);
          break;
      }
    }
    return classes.join(' ');
  });

  // Event handlers
  const onCloseHandler = () => {
    if (props.onClose) {
      props.onClose();
      return;
    }
  };

  // Form handlers - removed handleFormSave as it's now in individual modal components

  const handleUrlSave = async (item: RequestContent) => {
    // Update the modal state
    savedItem.value = item;

    // Ensure the custom URL is properly saved to backend using the store method
    // The EditURL modal should have already called updateRequestContent, but let's ensure persistence
    try {
      if (item.customUrl) {
        await requestContentStore.updateRequestContent(item.id, { customUrl: item.customUrl });
      }
    } catch (error) {
      console.error('Error saving custom URL to backend:', error);
    }

    // Return to previous view (either form or success)
    if (previousView.value) {
      currentView.value = previousView.value;
      previousView.value = null;
    } else {
      // Fallback to success if no previous view tracked
      currentView.value = MODAL_VIEWS.SUCCESS;
    }
  };

  // Navigation handlers
  const showSuccess = () => {
    currentView.value = MODAL_VIEWS.SUCCESS;
  };

  const showEditUrl = () => {
    previousView.value = currentView.value; // Remember where we came from
    currentView.value = MODAL_VIEWS.EDIT_URL;
  };

  const showPreviousView = () => {
    if (previousView.value) {
      currentView.value = previousView.value;
      previousView.value = null;
    } else {
      // Fallback to success if no previous view tracked
      currentView.value = MODAL_VIEWS.SUCCESS;
    }
  };

  // Collection picker handlers
  const handleCollectionSelect = (collection: CollectionItemInterface) => {
    selectedCollectionId.value = collection.id;
    selectedCollection.value = collection;
    closeCollectionPicker();
  };

  const closeCollectionPicker = () => {
    showCollectionPicker.value = false;
  };

  const openCollectionPicker = () => {
    showCollectionPicker.value = true;
  };

  return {
    // State
    currentView,
    savedItem,
    showCollectionPicker,
    selectedCollectionId,
    selectedCollection,
    slideClass,

    // Handlers
    onCloseHandler,
    handleUrlSave,
    showSuccess,
    showEditUrl,
    showPreviousView,
    handleCollectionSelect,
    closeCollectionPicker,
    openCollectionPicker,
  };
};
